import { BLOCK } from "../components/organisms/ContentRenderer/blockTypes";
import { listItemText } from "../components/organisms/ContentRenderer/blocks/TextBlocks";
import { RichContentData } from "../types/richContent";

/**
 * The ONE canonical "readable text in reading order" walker for authored content.
 * Replaces three ad-hoc walkers (DisplayContent TTS, SwiperOnboarding pageToText,
 * LegalMentionModal PDF) that each covered a different subset of block types.
 *
 * Rules:
 *  - every text-bearing slot is included, in block order (headers, paragraphs, list
 *    items, table cells, image/audio/video/iframe captions, hero title+subtitle, card
 *    titles+texts, icon-link labels, CTA labels — the label is read as-is: this util
 *    stays i18n-free, so no hardcoded "button:" prefixes in any language);
 *  - purely visual/media blocks contribute nothing beyond their caption;
 *  - inline HTML (<b>/<mark>/<a>…) is stripped.
 */

const stripHtml = (html?: string): string => {
  if (!html) return "";
  const div = document.createElement("div");
  div.innerHTML = html;
  const text = div.textContent ?? "";
  div.remove();
  return text.trim();
};

// Flattens list items INCLUDING nested sub-items ({content, items} shapes) — the legal
// PDF used to recurse into nested lists and must not lose them.
const flattenListItems = (items: unknown[]): string[] =>
  (items ?? []).flatMap((item: any) => [
    stripHtml(listItemText(item)),
    ...(Array.isArray(item?.items) ? flattenListItems(item.items) : []),
  ]);

/**
 * `pdf` mode keeps the language-neutral STRUCTURE decorations the legal PDF has always
 * had (list "- " bullets, " | " table separators, delimiter rules); the TTS mode stays
 * decoration-free so the voice never reads punctuation art.
 */
const blockToStrings = (type: string, data: Record<string, any>, pdf = false): string[] => {
  switch (type) {
    case BLOCK.HEADER:
    case BLOCK.PARAGRAPH:
      return [stripHtml(data?.text)];

    case BLOCK.LIST:
      return flattenListItems(data?.items ?? []).map((text) => (pdf && text ? `- ${text}` : text));

    case BLOCK.TABLE:
      return (data?.content ?? []).map((row: string[]) =>
        (row ?? []).map((cell) => stripHtml(cell)).filter(Boolean).join(pdf ? " | " : ", ")
      );

    case BLOCK.IMAGE:
    case BLOCK.EMBED:
      return [stripHtml(data?.caption)];

    case BLOCK.HERO:
      return [stripHtml(data?.title), stripHtml(data?.subtitle)];

    case BLOCK.CARDS:
      return (data?.items ?? []).flatMap((item: { title?: string; text?: string }) => [
        stripHtml(item?.title),
        stripHtml(item?.text),
      ]);

    case BLOCK.ICON_LINKS:
      return (data?.items ?? []).map((item: { label?: string }) => stripHtml(item?.label));

    case BLOCK.CTA_BUTTON:
      return [stripHtml(data?.label)];

    case BLOCK.AUDIO:
    case BLOCK.VIDEO:
    case BLOCK.IFRAME:
    // videoCards: the card titles live on the videos themselves, not in the content —
    // only the block's own caption is authored text.
    case BLOCK.VIDEO_CARDS:
      return [stripHtml(data?.caption)];

    case BLOCK.CALLOUT:
      return [stripHtml(data?.text)];

    case BLOCK.QUOTE:
      return [stripHtml(data?.text), stripHtml(data?.caption)];

    case BLOCK.SPACER:
      // Pure layout — nothing to read.
      return [];

    case BLOCK.DELIMITER:
      // Section rule in the PDF; silent for TTS.
      return pdf ? ["--------------------"] : [];

    // Unknown/future types: read their text field if they have one, else nothing.
    default:
      return [stripHtml(data?.text)];
  }
};

/** Per-block readable strings, empty entries dropped — the TTS feed. */
export const toParagraphs = (data?: RichContentData | null): string[] =>
  (data?.blocks ?? []).flatMap((block) => blockToStrings(block?.type, block?.data ?? {})).filter(Boolean);

/** PDF variant: same walk, with the structural decorations the legal export has always had. */
export const toPdfParagraphs = (data?: RichContentData | null): string[] =>
  (data?.blocks ?? []).flatMap((block) => blockToStrings(block?.type, block?.data ?? {}, true)).filter(Boolean);

/** Single string for vocalisation (TTS) — sentences separated so the reader pauses. */
export const toPlainText = (data?: RichContentData | null): string =>
  toParagraphs(data).join(".\n");
